import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EventHistoryEntry } from '../event-history/event-history-table.component';

export interface EventChangeRecord extends EventHistoryEntry { event_id: string; }

// Lifecycle grouping structure
interface LifecycleEntry {
  timestamp: string | null;
  user: string;
  event_id: string;
  changes: EventChangeRecord[];
  summary: string;
  fieldsChanged: number;
}

@Component({
  selector: 'app-event-version-history-dialog',
  templateUrl: './event-version-history-dialog.component.html',
  styleUrls: ['./event-version-history-dialog.component.scss'],
  standalone: false
})
export class EventVersionHistoryDialogComponent {
  changes: EventChangeRecord[] = [];
  lifecycle: LifecycleEntry[] = [];
  expanded: Set<number> = new Set();
  loading = true;
  error: string | null = null;
  /** Earliest recorded timestamp for this event's history, used as created date */
  createdAt: Date | null = null;

  constructor(
    private dialogRef: MatDialogRef<EventVersionHistoryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { eventId: string; loader: () => Promise<EventChangeRecord[]> }
  ) {
    this.load();
  }

  async load() {
    this.loading = true;
    this.error = null;
    try {
      this.changes = await this.data.loader();
      this.buildLifecycle();
    } catch (e: any) {
      this.error = e?.message || 'Failed to load version history';
    } finally {
      this.loading = false;
    }
  }

  private buildLifecycle() {
    if (!this.changes.length) { this.lifecycle = []; this.createdAt = null; return; }
    const sorted = [...this.changes].sort((a,b)=>{
      const at = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const bt = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return bt - at; // newest first
    });
    const groups: LifecycleEntry[] = [];
    const bucketKey = (c: EventChangeRecord) => {
      if (!c.timestamp) return c.user+':0';
      const d = new Date(c.timestamp);
      return `${c.user}:${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}-${d.getUTCHours()}-${d.getUTCMinutes()}`;
    };
    let currentKey: string | null = null;
    let current: LifecycleEntry | null = null;
    for (const change of sorted) {
      const key = bucketKey(change);
      if (key !== currentKey) {
        if (current) groups.push(current);
        currentKey = key;
        current = { timestamp: change.timestamp, user: change.user, event_id: change.event_id, changes: [], summary: '', fieldsChanged: 0 };
      }
      current!.changes.push(change);
    }
    if (current) groups.push(current);
    groups.forEach(g => {
      g.fieldsChanged = g.changes.length;
      const fieldNames = g.changes.map(c=> c.fieldLabel || c.field);
      if (fieldNames.length === 1) g.summary = `${fieldNames[0]} changed`;
      else if (fieldNames.length === 2) g.summary = `${fieldNames[0]} & ${fieldNames[1]} changed`;
      else g.summary = `${fieldNames[0]}, ${fieldNames[1]} + ${fieldNames.length-2} more changed`;
    });
    const isBlank = (v: unknown) => v === null || v === undefined || String(v).trim() === '';
    // Remove entries with no meaningful changes
    for (let i = groups.length - 1; i >= 0; i--) {
      const g = groups[i];
      if (g.changes.every(c => isBlank(c.oldValue) && isBlank(c.newValue))) {
        groups.splice(i, 1);
      }
    }
    this.lifecycle = groups;

    // Derive the earliest recorded timestamp as the event's created date
    const allTs = this.changes
      .map(c => c.timestamp ? new Date(c.timestamp).getTime() : null)
      .filter((v): v is number => v !== null);
    this.createdAt = allTs.length ? new Date(Math.min(...allTs)) : null;
    console.log("Lifecycle", this.lifecycle);
  }

  toggleExpand(idx: number) { this.expanded.has(idx) ? this.expanded.delete(idx) : this.expanded.add(idx); }

  close() { this.dialogRef.close(); }
}
